import React, { useState, useMemo } from 'react';

import { UnitCard, UnitFilters } from '../../index';
import { useSiteQuery } from '../../../hooks';
import { UnitFilterState } from '../../UnitFilters';

import query from './index.graphql';

import styles from './styles.module.scss';

interface Props {
  location: string;
  except?: string;
}

const Units = ({ location, except }: Props) => {
  const [{ data }] = useSiteQuery<UnitsQuery>({ query, variables: { location } });
  const entries = data?.entries;
  const locationTitle = entries?.[0]?.retirementVillageLocation?.[0]?.title;

  // Filter out the 'except' unit
  const allUnits = useMemo(() => {
    return entries?.filter((item) => item?.slug !== except) || [];
  }, [entries, except]);

  // Calculate min and max prices and available options
  const { minPrice, maxPrice, availableBeds, availableBaths, availableCarParks } = useMemo(() => {
    const prices = allUnits.map((unit) => unit?.price || 0).filter((p) => p > 0);

    // Get unique values for beds, baths, and car parks
    const bedsSet = new Set<number>();
    const bathsSet = new Set<number>();
    const carParksSet = new Set<number>();

    allUnits.forEach((unit) => {
      if (unit?.numberOfBeds) bedsSet.add(unit.numberOfBeds);
      if (unit?.numberOfBaths) bathsSet.add(unit.numberOfBaths);
      if (unit?.numberOfCarBays) carParksSet.add(unit.numberOfCarBays);
    });

    return {
      minPrice: prices.length > 0 ? Math.min(...prices) : 0,
      maxPrice: prices.length > 0 ? Math.max(...prices) : 1000000,
      availableBeds: Array.from(bedsSet).sort((a, b) => a - b),
      availableBaths: Array.from(bathsSet).sort((a, b) => a - b),
      availableCarParks: Array.from(carParksSet).sort((a, b) => a - b),
    };
  }, [allUnits]);

  const [filters, setFilters] = useState<UnitFilterState>({
    priceMin: minPrice,
    priceMax: maxPrice,
    beds: [],
    baths: [],
    carParks: [],
    sortBy: 'featured',
  });

  // Apply filters and sorting
  const filteredUnits = useMemo(() => {
    let filtered = [...allUnits];

    // Filter by price range
    filtered = filtered.filter((unit) => {
      const price = unit?.price || 0;
      return price >= filters.priceMin && price <= filters.priceMax;
    });

    // Filter by beds (1+ means >= 1, 2+ means >= 2, etc.)
    if (filters.beds.length > 0) {
      filtered = filtered.filter((unit) => {
        const beds = unit?.numberOfBeds || 0;
        return filters.beds.some((minBeds) => beds >= minBeds);
      });
    }

    // Filter by baths
    if (filters.baths.length > 0) {
      filtered = filtered.filter((unit) => {
        const baths = unit?.numberOfBaths || 0;
        return filters.baths.some((minBaths) => baths >= minBaths);
      });
    }

    // Filter by car parks
    if (filters.carParks.length > 0) {
      filtered = filtered.filter((unit) => {
        const carParks = unit?.numberOfCarBays || 0;
        return filters.carParks.some((minCarParks) => carParks >= minCarParks);
      });
    }

    // Sort
    if (filters.sortBy === 'price-asc') {
      filtered.sort((a, b) => (a?.price || 0) - (b?.price || 0));
    } else if (filters.sortBy === 'price-desc') {
      filtered.sort((a, b) => (b?.price || 0) - (a?.price || 0));
    }
    // 'featured' keeps the original order

    return filtered;
  }, [allUnits, filters]);

  const handleFilterChange = (newFilters: UnitFilterState) => {
    setFilters(newFilters);
  };

  if (allUnits.length > 0) {
    return (
      <div className="section">
        <div className="container">
          <div className="row">
            <div className={styles.cardIntro}>
              <p className={styles.preheading}>
                Available units
              </p>
              <h2 id="available-units">
                {locationTitle ? `View available units at ${locationTitle}` : 'Available Units'}
              </h2>
              <p className={styles.subheading}>
                Discover the range of options available.
              </p>
            </div>

            <UnitFilters
              onFilterChange={handleFilterChange}
              resultCount={filteredUnits.length}
              totalCount={allUnits.length}
              minPrice={minPrice}
              maxPrice={maxPrice}
              availableBeds={availableBeds}
              availableBaths={availableBaths}
              availableCarParks={availableCarParks}
            />

            {filteredUnits.length === 0 ? (
              <div className={styles.noResults}>
                <p>No matching units found</p>
              </div>
            ) : (
              <div className={`${styles.cardGrid} cardGrid`}>
                {filteredUnits.map((item) => item && <UnitCard key={item.uid!} item={item} />)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Units;
